import { useCallback, useEffect, useState } from "react";
import { captureError } from "@/lib/sentry";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { LANGUAGES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { SparkleIcon, TrashIcon } from "@phosphor-icons/react";

export default function Storylines() {
  const { user } = useAuth();
  const [topics, setTopics] = useState([]);
  const [items, setItems] = useState([]);
  const [language, setLanguage] = useState(user?.language_preference || "en");
  const [loading, setLoading] = useState(false);
  const [customTopic, setCustomTopic] = useState("");
  const [customCategory, setCustomCategory] = useState("custom");

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/storylines");
      setItems(r.data || []);
    } catch (err) {
      captureError("failed to load storylines:", err);
    }
  }, []);

  useEffect(() => {
    api.get("/storylines/topics")
      .then((r) => setTopics(r.data || []))
      .catch((err) => captureError("failed to load topics:", err));
    refresh();
  }, [refresh]);

  const generate = async (topic, category) => {
    setLoading(true);
    try {
      await api.post("/storylines/generate", { topic, category, language });
      await refresh();
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout>
      <div className="mb-6 flex items-end justify-between gap-3 fade-up">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#32D74B] mb-2">AI Storylines</p>
          <h1 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tighter">The Bigger Picture</h1>
        </div>
        <select
          data-testid="story-lang"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="bg-black border border-white/15 text-white/80 text-xs uppercase font-bold tracking-wider rounded-md px-3 py-2"
        >
          {LANGUAGES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
      </div>

      <div className="mb-8">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/40 mb-3">One-tap narratives</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {topics.map((t) => (
            <button
              key={t.category}
              disabled={loading}
              data-testid={`story-topic-${t.category}`}
              onClick={() => generate(t.topic, t.category)}
              className="p-3 rounded-md bg-[#121212] border border-white/10 hover:border-[#0A84FF]/60 text-left transition-colors disabled:opacity-50"
            >
              <SparkleIcon weight="bold" size={14} className="text-[#0A84FF] mb-1.5" />
              <p className="font-display font-bold uppercase text-xs tracking-tight text-white/90">{t.label}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="mb-8 p-4 rounded-md bg-[#121212] border border-white/10">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/40 mb-3">Custom storyline</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            data-testid="custom-story-topic"
            value={customTopic}
            onChange={(e) => setCustomTopic(e.target.value)}
            placeholder="e.g. Why the Wemby vs Chet rookie battle matters"
            className="flex-1 bg-black border border-white/15 focus:border-[#0A84FF] outline-none rounded-md px-3 py-2 text-sm"
          />
          <select
            value={customCategory}
            onChange={(e) => setCustomCategory(e.target.value)}
            data-testid="custom-story-category"
            className="bg-black border border-white/15 text-white/80 text-xs uppercase font-bold tracking-wider rounded-md px-3 py-2"
          >
            <option value="custom">Custom</option>
            <option value="rivalry">Rivalry</option>
            <option value="rookie_breakout">Rookie</option>
            <option value="playoff_implications">Playoffs</option>
            <option value="milestone_watch">Milestone</option>
            <option value="fantasy_impact">Fantasy</option>
            <option value="card_market">Cards</option>
            <option value="boricua_beat">Boricua Beat</option>
            <option value="dominican_dominance">Dominican</option>
          </select>
          <button
            data-testid="custom-story-btn"
            onClick={() => customTopic && generate(customTopic, customCategory)}
            disabled={loading || !customTopic}
            className="px-4 py-2 rounded-md bg-[#0A84FF] text-white text-xs font-bold uppercase tracking-wider hover:bg-blue-600 disabled:opacity-40"
          >
            {loading ? "Generating…" : "Generate"}
          </button>
        </div>
      </div>

      <h2 className="font-display font-bold text-2xl uppercase tracking-tighter mb-3">Your Storylines</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((s) => (
          <div key={s.id} className="p-5 rounded-md bg-[#121212] border border-white/10" data-testid={`storyline-${s.id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#32D74B]">{s.category}</span>
              <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/40">{s.language === "es" ? "ES" : "EN"}</span>
            </div>
            <h3 className="font-display font-bold text-xl uppercase mb-3 tracking-tight">{s.topic}</h3>
            <pre className="whitespace-pre-wrap text-sm text-white/85 font-sans leading-relaxed">{s.text}</pre>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-white/50 text-sm">No storylines yet — tap a chip above to generate one.</div>
        )}
      </div>
    </AppLayout>
  );
}
